// src/pages/HomePage.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase/firebase-config";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { Button, Container, Row, Col, Modal, Spinner, Alert } from 'react-bootstrap';
import { 
  FaPlus, FaSignOutAlt, FaSearch, FaHome, 
  FaGraduationCap, FaChalkboardTeacher, FaBookReader, FaList,
  FaCompass, FaCheckCircle, FaHourglassHalf, FaBookmark,
  FaUser, FaLink, FaTimes, FaFileAlt
} from 'react-icons/fa';
import '../styles/Dashboard.css';
import CourseCard from "../components/BookCard"; // We'll keep the file name for now but use it as CourseCard

function HomePage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [error, setError] = useState(null);
  
  // Get the location to check for URL hash
  const location = useLocation();
  const hashValue = location.hash.replace('#', '');
  
  // Initialize activeCategory based on URL hash if available
  const [activeCategory, setActiveCategory] = useState(
    hashValue === 'completed' ? 'completed' :
    hashValue === 'inprogress' ? 'inprogress' :
    hashValue === 'upcoming' ? 'upcoming' :
    hashValue === 'myinstructors' ? 'myinstructors' :
    hashValue === 'PBD-CS' ? 'PBD-CS' :
    hashValue === 'PDD-Marketing' ? 'PDD-Marketing' :
    hashValue === 'Bachelor-English' ? 'Bachelor-English' : 'all'
  );

  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const navigate = useNavigate();

  // Update URL hash when active category changes
  useEffect(() => {
    if (activeCategory === 'all') {
      navigate('/', { replace: true });
    } else {
      navigate(`/#${activeCategory}`, { replace: true });
    }
  }, [activeCategory, navigate]);

  // Fetch courses from Firestore
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const coursesRef = collection(db, "books"); // Still using "books" collection for now
        const querySnapshot = await getDocs(coursesRef);
        
        const courseData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log("Fetched courses:", courseData);
        setCourses(courseData);
        setError(null);
      } catch (err) {
        console.error("Error fetching courses:", err);
        setError("Failed to load courses. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // Clear localStorage
      localStorage.removeItem("uid");
      localStorage.removeItem("isAdmin");
      // Redirect to login page
      navigate("/login");
    } catch (error) {
      console.error("Error signing out: ", error);
      setError("Failed to sign out. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await deleteDoc(doc(db, "books", itemToDelete));
      setCourses(courses.filter(item => item.id !== itemToDelete));
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (err) {
      console.error("Error deleting course:", err);
      setError("Failed to delete course. Please try again.");
    }
  };

  // Filter based on activeCategory
  const filterCoursesByCategory = (courses) => {
    // For the main dashboard
    if (activeCategory === "all") {
      return courses;
    }
    // For status categories (inprogress, completed, upcoming), filter by course status
    if (["inprogress", "completed", "upcoming"].includes(activeCategory)) {
      return courses.filter(course => 
        course.readingStatus === activeCategory || course.status === activeCategory
      );
    }
    // For program categories (PBD-CS, PDD-Marketing, Bachelor-English), filter by program
    if (["PBD-CS", "PDD-Marketing", "Bachelor-English"].includes(activeCategory)) {
      return courses.filter(course => course.program === activeCategory);
    }
    // Default case
    return courses;
  };

  // Filter the courses based on search query and active category
  const filteredCourses = filterCoursesByCategory(
    courses.filter(item => {
      const matchesTitle = item.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTags = item.tags?.some(tag => 
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesInstructor = item.author?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesTitle || matchesTags || matchesInstructor;
    })
  );

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <FaGraduationCap className="me-2" /> Course Manager
          </div>
        </div>

        <div className="sidebar-nav">
          <div className="nav-item">
            <a 
              href="#" 
              className={`nav-link ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('all')}
            >
              <FaHome className="nav-icon" /> Dashboard
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="sidebar-heading">Actions</div>
          
          <div className="nav-item">
            <a 
              href="#" 
              className="nav-link add-course-button"
              onClick={() => navigate("/course/new")}
              style={{
                background: 'linear-gradient(135deg, #20bf55 0%, #01baef 100%)',
                color: 'white',
                borderRadius: '4px',
                padding: '10px 15px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                marginTop: '8px',
                marginBottom: '8px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
            >
              <FaPlus className="nav-icon" /> Add New Course
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="sidebar-heading">My Courses</div>
          
          <div className="nav-item">
            <a 
              href="#" 
              className={`nav-link ${activeCategory === 'completed' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('completed')}
            >
              <FaCheckCircle className="nav-icon" /> Completed
            </a>
          </div>
          
          <div className="nav-item">
            <a 
              href="#" 
              className={`nav-link ${activeCategory === 'inprogress' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('inprogress')}
            >
              <FaChalkboardTeacher className="nav-icon" /> In Progress
            </a>
          </div>
          
          <div className="nav-item">
            <a 
              href="#" 
              className={`nav-link ${activeCategory === 'upcoming' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('upcoming')}
            >
              <FaBookmark className="nav-icon" /> Upcoming
            </a>
          </div>
          
          <div className="sidebar-divider"></div>
          
          <div className="sidebar-heading">Programs</div>
          
          <div className="nav-item">
            <a 
              href="#" 
              className={`nav-link ${activeCategory === 'PBD-CS' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('PBD-CS')}
            >
              <FaGraduationCap className="nav-icon" /> PBD-CS
            </a>
          </div>
          
          <div className="nav-item">
            <a 
              href="#" 
              className={`nav-link ${activeCategory === 'PDD-Marketing' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('PDD-Marketing')}
            >
              <FaGraduationCap className="nav-icon" /> PDD-Marketing
            </a>
          </div>
          
          <div className="nav-item">
            <a 
              href="#" 
              className={`nav-link ${activeCategory === 'Bachelor-English' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('Bachelor-English')}
            >
              <FaGraduationCap className="nav-icon" /> Bachelor-English
            </a>
          </div>

          <div className="sidebar-divider"></div>
          
          <div className="nav-item">
            <a href="#" className="nav-link" onClick={handleSignOut}>
              <FaSignOutAlt className="nav-icon" /> Sign Out
            </a>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Container fluid style={{ maxWidth: '1400px' }}>
          {/* Error Alert */}
          {error && (
            <Alert variant="danger" className="mb-4 mt-4" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {/* Clean, Centered Dashboard */}
          <div className="dashboard-content py-5">
            <Row className="justify-content-center">
              <Col md={10} lg={8} xl={7} className="text-center">
                <div className="mb-5">
                  <h1 className="display-4 fw-bold mb-3" style={{ 
                    background: 'linear-gradient(135deg, #2c3e50, #4ca1af)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: '700'
                  }}>
                    {activeCategory === 'all' && 'Course Dashboard'}
                    {activeCategory === 'completed' && 'Completed Courses'}
                    {activeCategory === 'inprogress' && 'Courses in Progress'}
                    {activeCategory === 'upcoming' && 'Upcoming Courses'}
                    {activeCategory === 'myinstructors' && 'Instructors'}
                    {activeCategory === 'PBD-CS' && 'PBD-CS Program'}
                    {activeCategory === 'PDD-Marketing' && 'PDD-Marketing Program'}
                    {activeCategory === 'Bachelor-English' && 'Bachelor-English Program'}
                  </h1>
                  <p className="lead text-muted mb-4">
                    Find, explore, and manage your course collection
                  </p>
                </div>

                {/* Search Bar - Large and Centered */}
                <div className="search-wrapper mb-5">
                  <div className="position-relative mb-4" style={{ maxWidth: '700px', margin: '0 auto' }}>
                    <div className="search-box-container" style={{ 
                      position: 'relative',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      borderRadius: '16px',
                      overflow: 'hidden'
                    }}>
                      <FaSearch className="position-absolute" style={{ 
                        left: '20px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        fontSize: '1.5rem',
                        color: '#6c757d'
                      }} />
                      <input
                        type="text"
                        className="form-control form-control-lg ps-5"
                        placeholder="Search courses by title, instructor, or tag..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ 
                          padding: '1.2rem 1.2rem 1.2rem 3.5rem',
                          fontSize: '1.1rem',
                          border: 'none',
                          borderRadius: '16px'
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Quick category pills */}
                  <div className="category-pills d-flex flex-wrap justify-content-center gap-2 mb-4">
                    <Button 
                      variant={activeCategory === 'all' ? 'primary' : 'outline-primary'} 
                      className="rounded-pill px-4"
                      onClick={() => handleCategoryChange('all')}
                    >
                      All Courses
                    </Button>
                    <Button 
                      variant={activeCategory === 'completed' ? 'success' : 'outline-success'} 
                      className="rounded-pill px-4"
                      onClick={() => handleCategoryChange('completed')}
                    >
                      Completed
                    </Button>
                    <Button 
                      variant={activeCategory === 'inprogress' ? 'info' : 'outline-info'} 
                      className="rounded-pill px-4"
                      onClick={() => handleCategoryChange('inprogress')}
                    >
                      In Progress
                    </Button>
                    <Button 
                      variant={activeCategory === 'upcoming' ? 'secondary' : 'outline-secondary'} 
                      className="rounded-pill px-4"
                      onClick={() => handleCategoryChange('upcoming')}
                    >
                      Upcoming
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-container">
                      <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </div>
                    <p className="lead mt-4">Loading your courses...</p>
                  </div>
                ) : filteredCourses.length > 0 ? (
                  <div className="search-results text-start mt-4">
                    <h4 className="mb-4">
                      {searchQuery ? (
                        <>Search Results <small className="text-muted">({filteredCourses.length} courses found)</small></>
                      ) : (
                        <>{filteredCourses.length} {filteredCourses.length === 1 ? 'Course' : 'Courses'} {activeCategory !== 'all' && `in ${
                          activeCategory === 'completed' ? 'Completed' :
                          activeCategory === 'inprogress' ? 'In Progress' :
                          activeCategory === 'PBD-CS' ? 'PBD-CS' :
                          activeCategory === 'PDD-Marketing' ? 'PDD-Marketing' :
                          activeCategory === 'Bachelor-English' ? 'Bachelor-English' :
                          activeCategory === 'upcoming' ? 'Upcoming' : ''
                        }`}</>
                      )}
                    </h4>
                    <div className="result-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {filteredCourses.map((course) => (
                        <div 
                          key={course.id} 
                          className="search-result-item p-3 mb-3 d-flex align-items-center"
                          style={{ 
                            borderRadius: '12px',
                            backgroundColor: '#f8f9fa',
                            transition: 'all 0.2s ease',
                            cursor: 'default', /* Changed from pointer to default */
                            border: '1px solid #eaeaea'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f1f3f5'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        >
                          <div 
                            className="me-3" 
                            style={{ 
                              width: '50px', 
                              height: '50px', 
                              borderRadius: '10px', 
                              overflow: 'hidden',
                              backgroundColor: '#e9ecef',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer', /* Add cursor pointer */
                            }}
                            onClick={() => navigate(`/course/${course.id}/edit`)}
                            title="Edit Course"
                          >
                            {course.imageUrl ? (
                              <img src={course.imageUrl} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <FaGraduationCap style={{ fontSize: '1.5rem', color: '#adb5bd' }} />
                            )}
                          </div>
                          <div 
                            style={{ flexGrow: 1 }}
                            onClick={() => navigate(`/course/${course.id}/edit`)}
                            className="cursor-pointer"
                          >
                            <h6 className="mb-0">{course.title}</h6>
                            <small className="text-muted">{course.professor || course.author}</small>
                          </div>
                          <div className="d-flex gap-2">
                            {course.courseWebsite && (
                              <Button 
                                size="sm" 
                                variant="outline-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(course.courseWebsite, '_blank');
                                }}
                                title="Visit Website"
                              >
                                <FaLink />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(course.id);
                              }}
                              title="Delete Course"
                            >
                              <FaTimes />
                            </Button>
                            {course.courseNotesUrl && (
                              <Button 
                                size="sm" 
                                variant="outline-info"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(course.courseNotesUrl, '_blank');
                                }}
                                title="Course Notes"
                              >
                                <FaFileAlt />
                              </Button>
                            )}
                            {course.program && (
                              <span className="badge bg-primary align-self-center">{course.program}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="empty-state py-5">
                    <div className="empty-icon mb-4">
                      {activeCategory === 'all' ? (
                        <FaSearch style={{ fontSize: '3.5rem', color: '#adb5bd' }} />
                      ) : activeCategory === 'completed' ? (
                        <FaCheckCircle style={{ fontSize: '3.5rem', color: '#adb5bd' }} />
                      ) : activeCategory === 'inprogress' ? (
                        <FaChalkboardTeacher style={{ fontSize: '3.5rem', color: '#adb5bd' }} />
                      ) : (
                        <FaBookmark style={{ fontSize: '3.5rem', color: '#adb5bd' }} />
                      )}
                    </div>
                    <h4 className="text-muted mb-3">
                      {searchQuery 
                        ? 'No courses match your search' 
                        : activeCategory !== 'all'
                          ? `No courses in ${
                              activeCategory === 'completed' ? 'Completed' :
                              activeCategory === 'inprogress' ? 'In Progress' :
                              activeCategory === 'myinstructors' ? 'Instructors' :
                              activeCategory === 'PBD-CS' ? 'PBD-CS' :
                              activeCategory === 'PDD-Marketing' ? 'PDD-Marketing' :
                              activeCategory === 'Bachelor-English' ? 'Bachelor-English' :
                              'Upcoming'
                            } category`
                          : ''
                      }
                    </h4>
                    {searchQuery ? (
                      <p className="text-muted">Try using different keywords or check your filters</p>
                    ) : activeCategory === 'all' && (
                      <p className="text-muted">
                      </p>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </Container>
      </main>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this course? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default HomePage;
